import React, { useCallback, useMemo, useState } from 'react';
import { useGetDepartmentsQuery, useGetStoresQuery } from '../../../apis/apiLists';
import { useGetYPickCredentialsQuery } from './apiYPickCredentials';
import {
  getStoreDepartmentYPickCredentialsColumns,
  StoreDepartmentCredentials,
} from './yPickCredentialsLib';
import { YPICK_LABEL } from '../../../components/global/_constants';
import { ApplicationDataType } from '@ydistri/utils';
import { useApplicationData } from '../../../hooks/useApplicationData';
import {
  ActionsBar,
  DataTable,
  SearchBox,
  Section,
  useInjectLocalFilters,
  YColumnsType,
} from '@ydistri/ds';
import {
  EntityColumns,
  useStoreTableColumnsProvider,
} from '../../../hooks/useEntityTableColumnsProvider';
import DownloadAllQrCodesButton from './DownloadAllQrCodesButton';
import { computeRemSize } from '@ydistri/theme';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

const StoreDepartmentCredentialsSection: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { [ApplicationDataType.DEPARTMENTS]: hasDepartments } = useApplicationData();
  const storeColumnsProvider = useStoreTableColumnsProvider();
  const [search, setSearch] = useState<string>('');

  const { data: availableDepartments, isFetching: isFetchingAvailableDepartments } =
    useGetDepartmentsQuery(templateOrCalculation.value);
  const { data: availableStores, isFetching: isFetchingAvailableStores } = useGetStoresQuery(
    templateOrCalculation.value,
  );
  const { data: yPickCredentials, isFetching: isFetchingYPickCredentials } =
    useGetYPickCredentialsQuery();

  const columns = useMemo(() => {
    const codeStoreColumnConfig: YColumnsType<StoreDepartmentCredentials> = {
      width: '8rem',
    };

    const idStoreColumnConfig: YColumnsType<StoreDepartmentCredentials> = {
      width: computeRemSize(70),
      showSorterTooltip: false,
    };

    const storeColumnsConfig = {
      [EntityColumns.CODE]: codeStoreColumnConfig,
      [EntityColumns.ID]: idStoreColumnConfig,
    };
    const storeColumns = storeColumnsProvider<StoreDepartmentCredentials>(storeColumnsConfig);

    return getStoreDepartmentYPickCredentialsColumns(hasDepartments, storeColumns);
  }, [storeColumnsProvider, hasDepartments]);

  const data: StoreDepartmentCredentials[] = useMemo(() => {
    if (yPickCredentials && availableStores && availableDepartments) {
      const lowercaseSearch = search.toLowerCase();
      return yPickCredentials
        .map(ypc => ({
          storeId: ypc.storeId,
          store: {
            id: ypc.storeId,
            customerStoreId: availableStores.object[ypc.storeId]?.customerStoreId ?? '-',
            name: availableStores.object[ypc.storeId]?.name ?? '-',
            code: availableStores.object[ypc.storeId]?.code ?? '-',
          },
          department: {
            id: ypc.departmentId,
            customerDepartmentId:
              availableDepartments.object[ypc.departmentId]?.customerDepartmentId ?? '-',
            name: availableDepartments.object[ypc.departmentId]?.name ?? '-',
          },
          storeTypeName: availableStores.object[ypc.storeId]?.storeType?.name ?? '-',
          regionName: availableStores.object[ypc.storeId]?.region?.name ?? '-',
          fileName: ypc.qrCodeFileName,
        }))
        .filter(
          ypc =>
            ypc.store.name !== '-' &&
            (search.length > 0
              ? ypc.store.name.toLowerCase().includes(lowercaseSearch) ||
                ypc.store.customerStoreId.toLowerCase().includes(lowercaseSearch) ||
                ypc.store.code.toLowerCase().includes(lowercaseSearch)
              : true),
        );
    }
    return [];
  }, [availableDepartments, availableStores, yPickCredentials, search]);

  const filterableColumns = useInjectLocalFilters(columns);

  const loading =
    isFetchingAvailableDepartments || isFetchingAvailableStores || isFetchingYPickCredentials;

  const headerActions = useMemo(() => {
    return (
      <ActionsBar>
        <SearchBox
          value={search}
          onSearch={setSearch}
          placeholder="Search "
          id="ypick-credentials-search-box"
        />
        <DownloadAllQrCodesButton />
      </ActionsBar>
    );
  }, [search, setSearch]);

  const rowKeyProvider = useCallback(
    (record: StoreDepartmentCredentials) =>
      `${record.storeId}#${record.department.customerDepartmentId}`,
    [],
  );

  return (
    <Section header={`${YPICK_LABEL} Credentials`} headerActions={headerActions}>
      <DataTable<StoreDepartmentCredentials>
        showBorder={true}
        showHeader={true}
        id="store-configurations"
        loading={loading}
        columns={filterableColumns}
        dataSource={data}
        size="small"
        rowKey={rowKeyProvider}
      />
    </Section>
  );
};

export default StoreDepartmentCredentialsSection;
